/**
 * One undo order across several histories (2026-10-13, operator: "there's no undo").
 *
 * The timeline editor edits two kinds of thing: the timeline (a local document, `timeline` history) and the song
 * under it (beat map fields OPBridge keeps, `song` history). Each history keeps its own stack, so on its own neither
 * knows which edit came LAST. This log records the order of checkpoints, so Ctrl+Z in the editor undoes the most
 * recent edit whichever history holds it, and Ctrl+Y redoes in the reverse order.
 */
import type { HistoryHandle } from './history.ts';

const handles = new Map<string, HistoryHandle<unknown>>();
const order: string[] = [];
const redoOrder: string[] = [];
const MAX = 400;

/** Register a history that takes part in the shared order. */
export function registerOrdered(name: string, h: HistoryHandle<unknown>): void {
  handles.set(name, h);
}

/** A new edit landed in `name`'s history (its `onCheckpoint`). Any new edit ends the redo chain. */
export function noteEdit(name: string): void {
  order.push(name);
  if (order.length > MAX) order.splice(0, order.length - MAX);
  redoOrder.length = 0;
}

/** Undo the most recent edit across the ordered histories. */
export function undoLatest(): { ok: boolean; name: string } {
  while (order.length) {
    const name = order.pop()!;
    const h = handles.get(name);
    if (h && h.undoDepth() > 0 && h.undo()) { redoOrder.push(name); return { ok: true, name }; }
  }
  // An edit older than the log (or a log trimmed at MAX): fall back to any history with something to undo.
  for (const [name, h] of handles) if (h.undoDepth() > 0 && h.undo()) { redoOrder.push(name); return { ok: true, name }; }
  return { ok: false, name: '' };
}

/** Redo the most recently undone edit. */
export function redoLatest(): { ok: boolean; name: string } {
  while (redoOrder.length) {
    const name = redoOrder.pop()!;
    const h = handles.get(name);
    if (h && h.redoDepth() > 0 && h.redo()) { order.push(name); return { ok: true, name }; }
  }
  for (const [name, h] of handles) if (h.redoDepth() > 0 && h.redo()) { order.push(name); return { ok: true, name }; }
  return { ok: false, name: '' };
}

/** Anything to undo / redo in the ordered histories (reactive: reads the depth signals). */
export function canUndoOrdered(): boolean { return [...handles.values()].some((h) => h.undoDepth() > 0); }
export function canRedoOrdered(): boolean { return [...handles.values()].some((h) => h.redoDepth() > 0); }
